import { useTerrain } from '../../terrain/terrain-store.js';
import { num, type ComponentForm } from '../component-types.js';

/**
 * Voxel-only fields: hidden without a data file and once the editor has
 * loaded it as a heightmap; always shown once set.
 */
function maybeVoxel(v: Record<string, unknown>): boolean {
  if (v.viewDistance !== undefined) return true;
  if (typeof v.data !== 'string' || !v.data) return false;
  const json = useTerrain.getState().files[v.data]?.json;
  return !json || json.type === 'voxel';
}

/**
 * The `terrain` component: a `*.terrain.json` data asset (heightmap or voxel)
 * placed at the entity's position (engine/docs/systems/terrain.md). Sculpt /
 * paint tools, layers and details are below the fields (TerrainEditor).
 */
const form: ComponentForm = {
  key: 'terrain',
  label: 'Terrain',
  icon: 'mountain',
  order: 90,
  defaults: {},
  fields: [
    {
      key: 'data',
      label: 'Data',
      hint: 'The *.terrain.json asset (heights / voxels, layers, details)',
      type: { kind: 'path', suffix: '.terrain.json' },
    },
    {
      key: 'material',
      label: 'Material',
      hint: 'Material for layers without their own (unset = standard, vertex colors as albedo)',
      type: { kind: 'path', suffix: '.material.json', allowObject: true },
    },
    {
      key: 'physicsMaterial',
      label: 'Physics material',
      type: { kind: 'path', suffix: '.physicsmat.json', allowObject: true },
    },
    { key: 'castShadows', label: 'Cast shadows', type: { kind: 'bool' }, read: () => true },
    {
      key: 'lodDistance',
      label: 'LOD distance',
      hint: 'Heightmap: meters per halving of the mesh resolution. Voxel: full detail radius, each coarser level doubles it (80)',
      type: num(5, { min: 1 }),
      read: () => 80,
    },
    {
      key: 'viewDistance',
      label: 'View distance',
      hint: 'Voxel: meters within which the terrain is drawn (600)',
      type: num(10, { min: 1 }),
      read: () => 600,
      showIf: maybeVoxel,
    },
    { key: 'detailDistance', label: 'Detail distance', hint: 'Details are drawn within this many meters (120)', type: num(5, { min: 0 }), read: () => 120 },
  ],
};
export default form;
